import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

/**
 * Starts every NEW page at the top.
 *
 * The app scrolls the window (AppLayout's content wrapper is in normal flow
 * under a sticky 52px header) and nothing reset that scroll on a route change:
 * no `<ScrollRestoration>`, no scroll-to-top. So a page opened from far down a
 * long page inherited that offset and rendered mid-page. Reported from the CRM
 * Setup hub, whose lower cards (Goals, Response SLAs, Automation, Data export,
 * Profile, Email signature, AI Governance, Lead sourcing) all opened scrolled
 * to wherever the hub had been, while the cards near the top "worked" only
 * because the reader was already at the top.
 *
 * Three deliberate exceptions, each a case where the reader's position IS the
 * point:
 *  - browser back / forward (`POP`): the browser restores the position it
 *    remembers, and resetting it would make Back land somewhere new;
 *  - a hash deep link (`/page#section`): the page owns that scroll;
 *  - a same-path navigation (a tab or filter written to the query string, a
 *    `replace` of the URL): the page did not change, so the position must not.
 *
 * A layout effect, not a passive one: the reset lands before paint (no flash of
 * the old offset), and before any page's own `useEffect(scrollIntoView)`, which
 * therefore still wins.
 */
export function ScrollToTopOnNavigate() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  // Seeded with the CURRENT pathname so the first render is a no-op: a fresh
  // load is already at the top, and scrolling it would be a visible jerk on a
  // hash-less deep link.
  //
  // ⚠️ That also means a REMOUNT reads as a first render — if AppLayout is ever
  // torn down and rebuilt across a navigation (a key change, an ErrorBoundary
  // reset), the ref comes back seeded to the NEW path and the reset is skipped.
  // Not reachable today: AppLayout sits above the router outlet and is not keyed,
  // so it persists across every in-app navigation. Recorded because the failure
  // would be silent and would look like the bug this component was written for,
  // and the fix if it ever happens is to lift the ref into a module-level value
  // rather than to distrust the guard.
  const lastPathname = useRef(pathname);

  useLayoutEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (navigationType === "POP" || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navigationType]);

  return null;
}
